import { useQuery } from '@apollo/client';
import { Image } from '@mui/icons-material';
import { Box, Button, Card, CardMedia, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { number, object, string } from 'yup';
import { useUpdateTemplate } from '~/api/templates';
import { DeleteButton, SaveButton } from '~/components/button';
import { DetailSelect, DetailTextArea, DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import type { TemplatePatch } from '~/generated/graphql';
import { toDataUri } from '~/lib/file';
import {
  TemplateSettingsPageDocument,
  type TemplateSettings__Template as Template,
  type TemplateSettingsPage,
} from '../queries/settings.generated';
import { Layout } from './components';

const validationSchema = object({
  name: string().default('').trim(),
  description: string().default('').trim(),
  templateCategoryId: number().integer().positive(),
  thumbnailUri: string().nullable(),
});

interface SettingsFormProps {
  template: Template;
  templateCategories: TemplateSettingsPage['templateCategories'];
}

const SettingsForm = ({ template, templateCategories }: SettingsFormProps) => {
  const { t } = useTranslation();
  const [updateTemplate] = useUpdateTemplate();

  const initialValues = {
    name: template.name,
    description: template.description,
    thumbnailUri: template.thumbnailUri,
    templateCategoryId: template.templateCategory.id,
  };

  const onSubmit = async (values: TemplatePatch) => {
    const patch: { -readonly [K in keyof TemplatePatch]: TemplatePatch[K] } = {};
    if (values.name !== initialValues.name) patch.name = values.name;
    if (values.description !== initialValues.description) patch.description = values.description;
    if (values.templateCategoryId !== initialValues.templateCategoryId)
      patch.templateCategoryId = values.templateCategoryId;
    if (values.thumbnailUri !== initialValues.thumbnailUri)
      patch.thumbnailUri = values.thumbnailUri;

    await updateTemplate({ variables: { templateId: template.id, patch } });
  };

  const formik = useFormik<TemplatePatch>({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema,
  });

  const readOnly = !template.canUpdate.value;
  const disabled = readOnly || formik.isSubmitting;

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const thumbnailUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('thumbnailUri', thumbnailUri, true);
    },
  });

  return (
    <Box component="form" noValidate onSubmit={formik.handleSubmit}>
      {!readOnly && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-end',
            paddingBottom: theme.spacing(1),
            [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
          })}
        >
          <SaveButton
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
            type="submit"
          >
            {t('common.actions.save')}
          </SaveButton>
        </Box>
      )}
      <div>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.name')}</SettingName>
              <SettingDescription>
                {t('pages.templates.settings.name_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                autoFocus={!readOnly}
                disabled={disabled}
                error={formik.touched.name && !!formik.errors.name}
                fullWidth
                helperText={(formik.touched.name && formik.errors.name) || ' '}
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.description')}</SettingName>
              <SettingDescription>
                {t('pages.templates.settings.description_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextArea
                error={formik.touched.description && !!formik.errors.description}
                disabled={disabled}
                fullWidth
                helperText={(formik.touched.description && formik.errors.description) || ' '}
                name="description"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.description}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.templates.settings.category_label')}</SettingName>
              <SettingDescription>
                {t('pages.templates.settings.category_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSelect
                name="templateCategoryId"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                disabled={disabled}
                value={formik.values.templateCategoryId ?? ''}
              >
                {templateCategories.map((category) => (
                  <MenuItem key={`template_category_${category.id}`} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </DetailSelect>
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.thumbnail')}</SettingName>
              <SettingDescription>
                {t('pages.templates.settings.thumbnail_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              {formik.values.thumbnailUri && (
                <Card
                  sx={{
                    maxWidth: '50%',
                    width: '100%',
                    maxHeight: 120,
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <CardMedia
                    alt={t('common.labels.current_thumbnail')}
                    image={formik.values.thumbnailUri}
                    crossOrigin="anonymous"
                    component="img"
                  />
                </Card>
              )}
              {!readOnly && (
                <>
                  <input {...getInputProps()} />
                  <Box display="flex" flexDirection="column" rowGap={1}>
                    <Button
                      color="primary"
                      onClick={selectFile}
                      startIcon={<Image />}
                      variant="contained"
                      disabled={disabled}
                    >
                      {formik.values.thumbnailUri
                        ? t('common.dialogs.update')
                        : t('pages.templates.settings.select_thumbnail_button')}
                    </Button>
                    {formik.values.thumbnailUri && (
                      <DeleteButton onClick={() => void formik.setFieldValue('thumbnailUri', '')}>
                        {t('common.dialogs.remove')}
                      </DeleteButton>
                    )}
                  </Box>
                </>
              )}
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>
    </Box>
  );
};

export const TemplateSettings = () => {
  const params = useParams<{ templateId: string }>();
  const templateId = Number(params.templateId);

  const { data, loading } = useQuery(TemplateSettingsPageDocument, { variables: { templateId } });

  return (
    <Layout>
      <LoadingPane in={loading && !data} size={40} thickness={4}>
        {data?.template && (
          <SettingsForm template={data.template} templateCategories={data.templateCategories} />
        )}
      </LoadingPane>
    </Layout>
  );
};
